import React, { type ReactNode } from 'react'
import { useProfile } from '../api/hooks'
import { authUtils } from '../api/auth'
import { useLocation } from 'react-router-dom'
import { useSessionExpiration } from '@/lib/hooks/useSessionExpiration'
import { useTokenRefresh } from '@/lib/hooks/useTokenRefresh'
import { AuthContext } from './useAuth'

/**
 * Renders nothing; just mounts the proactive refresh timer and the 401-event
 * listener for the entire authenticated lifetime. Must live inside the router
 * so `useSessionExpiration` can call `useNavigate`.
 */
const SessionManager: React.FC = () => {
  useTokenRefresh()
  useSessionExpiration()
  return null
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation()

  // Public pages that don't need user profile
  const publicPages = ['/', '/auth', '/login', '/verify-email']
  const isPublicPage = publicPages.includes(location.pathname)

  // Only fetch profile if:
  // 1. User has a token AND
  // 2. We're not on a public page
  const shouldFetchProfile = !!authUtils.getToken() && !isPublicPage

  const { data: user, isLoading } = useProfile({
    enabled: shouldFetchProfile,
  })

  const logout = () => {
    authUtils.removeToken()
    window.location.href = '/auth'
  }

  const isAuthenticated = authUtils.isAuthenticated()

  const value = {
    user: shouldFetchProfile ? user : null,
    isLoading: shouldFetchProfile ? isLoading : false,
    isAuthenticated,
    logout,
  }

  return (
    <AuthContext.Provider value={value}>
      {isAuthenticated && <SessionManager />}
      {children}
    </AuthContext.Provider>
  )
}
